import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authService } from '../../services/authService';

// Component for routes that should be accessible only when NOT authenticated
export const PublicRoute = ({ children, allowRegistration = true }: { children: JSX.Element, allowRegistration?: boolean }) => {
    const isAuthenticated = authService.isAuthenticated();
    const location = useLocation();

    // Liste des routes toujours accessibles, même si l'utilisateur est authentifié
    const alwaysAccessibleRoutes = ['/registration-success'];
    const isAlwaysAccessible = alwaysAccessibleRoutes.includes(location.pathname);

    // Si l'utilisateur est authentifié et que la route n'est pas dans la liste des exceptions
    if (isAuthenticated && !isAlwaysAccessible) {
        return <Navigate to="/dashboard" replace />;
    }

    // If this is the register route and direct registration is not allowed, redirect to landing page
    if (!allowRegistration && location.pathname === '/register' && !location.state?.fromPricing) {
        return <Navigate to="/" replace />;
    }

    return children;
};

// Component for routes that should be accessible only when authenticated
export const PrivateRoute = ({ children }: { children: JSX.Element }) => {
    const isAuthenticated = authService.isAuthenticated();
    const location = useLocation();
    const [isChecking, setIsChecking] = useState(isAuthenticated);
    const [isAccountActive, setIsAccountActive] = useState(true);

    // Au montage, on vérifie côté serveur que le compte n'a pas été désactivé/supprimé
    useEffect(() => {
        if (!isAuthenticated) return;
        let cancelled = false;

        authService.checkAccountStatus().then((active) => {
            if (cancelled) return;
            if (!active) {
                authService.logout();
                setIsAccountActive(false);
            }
            setIsChecking(false);
        });

        return () => { cancelled = true; };
    }, [isAuthenticated]);

    // If user is not authenticated, redirect to login
    if (!isAuthenticated && isAccountActive) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (!isAccountActive) {
        return <Navigate to="/login?reason=account_deactivated" replace />;
    }

    // Vérification en cours : rien à afficher pour éviter un flash de contenu protégé
    if (isChecking) {
        return null;
    }

    return children;
};
